import { useRef, useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, FileText, Loader2, RotateCcw, ShieldAlert, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { extractPdfText, readDecision, type DecisionDeadline, type DecisionReading } from '@/lib/decisionReader';

const MIN_TEXT = 200;

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="space-y-2">
    <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9a7f52]">{title}</h3>
    {children}
  </section>
);

const Quote = ({ text, confirmed }: { text: string; confirmed: boolean }) => (
  <blockquote className={`mt-2 border-l-2 pl-3 text-xs ${confirmed ? 'border-[#c4ab83] text-slate-600' : 'border-red-400 text-red-700'}`}>
    {text ? `"${text}"` : 'Sem trecho citado.'}
    {!confirmed && <span className="mt-1 block font-semibold">Este trecho não foi encontrado no documento. Não confie nesta informação sem conferir.</span>}
  </blockquote>
);

export const DecisionReader = ({ onUseDeadline }: { onUseDeadline: (deadline: DecisionDeadline, caseNumber: string | null) => void }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState('');
  const [fileName, setFileName] = useState('');
  const [consent, setConsent] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<DecisionReading | null>(null);

  const reset = () => {
    setSource('');
    setFileName('');
    setError('');
    setResult(null);
    setConsent(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onFile = async (file?: File) => {
    if (!file) return;
    setError('');
    setResult(null);
    setExtracting(true);
    try {
      const { text, pages } = await extractPdfText(file);
      if (text.length < MIN_TEXT) {
        setSource('');
        setFileName('');
        setError('Este PDF parece ser uma imagem escaneada, e não foi possível ler o texto. Copie o texto da decisão e cole no campo abaixo.');
        return;
      }
      setSource(text);
      setFileName(`${file.name} · ${pages} ${pages === 1 ? 'página' : 'páginas'}`);
    } catch (e) {
      setError((e as Error).message || 'Não foi possível abrir o PDF.');
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    setReading(true);
    setError('');
    try {
      setResult(await readDecision(source));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setReading(false);
    }
  };

  if (result) {
    return (
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9a7f52]">{result.tipoAto}{result.numeroProcesso ? ` · ${result.numeroProcesso}` : ''}</p>
            <h2 className="mt-1 text-lg font-semibold leading-snug text-slate-900">{result.resultado}</h2>
          </div>
          <Button variant="outline" size="sm" onClick={reset}><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Ler outra</Button>
        </div>

        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Leitura feita por IA, que pode errar. Confira na decisão original antes de agir. Prazos que a decisão não menciona, como os previstos em lei, não aparecem aqui.</p>
        </div>

        {result.resumo && <Section title="Resumo"><p className="text-sm leading-relaxed text-slate-700">{result.resumo}</p></Section>}

        <Section title="Prazos citados na decisão">
          {result.prazos.length === 0 ? (
            <p className="text-sm text-slate-500">A decisão não cita prazo expresso. Isso não significa que não haja prazo legal.</p>
          ) : (
            <ul className="space-y-3">
              {result.prazos.map((p, i) => (
                <li key={i} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      {p.dias ? `${p.dias} dias` : 'Dias não informados'}
                      {p.contagem !== 'nao_informado' && ` ${p.contagem === 'uteis' ? 'úteis' : 'corridos'}`}
                      {p.descricao && <span className="font-normal text-slate-600"> · {p.descricao}</span>}
                    </p>
                    <Button size="sm" variant="outline" disabled={!p.dias} onClick={() => onUseDeadline(p, result.numeroProcesso)}>
                      Calcular<ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Quote text={p.trecho} confirmed={p.trechoConfere} />
                </li>
              ))}
            </ul>
          )}
        </Section>

        {result.determinacoes.length > 0 && (
          <Section title="O que foi determinado">
            <ul className="space-y-1.5 text-sm text-slate-700">
              {result.determinacoes.map((d, i) => (
                <li key={i} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#9a7f52]" /><span>{d.quem && <strong className="font-semibold">{d.quem}: </strong>}{d.o_que}</span></li>
              ))}
            </ul>
          </Section>
        )}

        {result.audiencias.length > 0 && (
          <Section title="Audiências">
            <ul className="space-y-3">
              {result.audiencias.map((a, i) => (
                <li key={i} className="rounded-xl border border-slate-200 p-3 text-sm text-slate-800">
                  {a.data ? `${a.data.split('-').reverse().join('/')} · ` : ''}{a.descricao}
                  <Quote text={a.trecho} confirmed={a.trechoConfere} />
                </li>
              ))}
            </ul>
          </Section>
        )}

        {result.pontosDeAtencao.length > 0 && (
          <Section title="Pontos de atenção">
            <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
              {result.pontosDeAtencao.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </Section>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={extracting}
        className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-4 py-8 text-center transition-colors hover:border-[#c4ab83] hover:bg-[#c4ab83]/5"
      >
        {extracting ? <Loader2 className="h-7 w-7 animate-spin text-[#9a7f52]" /> : fileName ? <FileText className="h-7 w-7 text-[#9a7f52]" /> : <Upload className="h-7 w-7 text-[#9a7f52]" />}
        <span className="text-sm font-medium text-slate-800">{extracting ? 'Lendo o PDF...' : fileName || 'Escolher o PDF da decisão'}</span>
        <span className="text-xs text-slate-500">O PDF é aberto aqui no seu computador. Só o texto segue para a IA.</span>
      </button>
      <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />

      <div className="space-y-1.5">
        <Label htmlFor="decision-text">Ou cole o texto da decisão</Label>
        <Textarea
          id="decision-text"
          value={source}
          onChange={(e) => { setSource(e.target.value); setFileName(''); }}
          rows={6}
          className="resize-y text-sm"
          placeholder="Cole aqui o texto da decisão, do despacho ou da intimação"
        />
      </div>

      <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
        <span>
          <span className="flex items-center gap-1 font-semibold text-slate-900"><ShieldAlert className="h-3.5 w-3.5 text-[#9a7f52]" />Este texto será enviado ao Google (Gemini)</span>
          Confirmo que este documento pode ser processado por esse serviço. Atenção aos processos em segredo de justiça e aos dados de menores: se preferir, retire os nomes do texto antes de enviar. O sistema não guarda o texto nem o resultado.
        </span>
      </label>

      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <Button onClick={submit} disabled={reading || !consent || source.trim().length < MIN_TEXT} className="w-full bg-slate-900 text-white hover:bg-slate-800">
        {reading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Lendo a decisão...</> : 'Ler decisão'}
      </Button>
    </div>
  );
};
